import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";

import { ByTitle } from "../../../../model/filters/by-title";
import { MatchAny } from "../../../../model/filters/match.any";
import { Filter } from "../../../../model/filters/filter";
import { SingleChoice } from "../../../../model/singlechoice";
import { NotComponent } from "../not/not.component";

@Component({
  selector: "by-title-form",
  templateUrl: "./by-title-form.component.html",
  styleUrls: ["./by-title-form.component.css"],
})
export class ByTitleFormComponent implements OnInit, OnChanges {
  @Input() filter: ByTitle = new ByTitle("");
  @Output() applyFilter = new EventEmitter<Filter>();
  @Output() choiceEvent: EventEmitter<SingleChoice> = new EventEmitter();

  @ViewChild(NotComponent) notComponent!: NotComponent;
  byTitleFilter: ByTitle = new ByTitle("");
  byTitleForm: FormGroup;

  editingIndex: number | null = null;

  constructor(private fb: FormBuilder, private router: Router) {}

  ngOnInit(): void {
    const existingFilters =
      this.filter instanceof MatchAny ? this.filter.filters : [this.filter];

    const phrases: string[] = existingFilters
      .filter((f): f is ByTitle => f instanceof ByTitle)
      .map((f) => f.searchphrase)
      .filter(
        (phrase) => phrase !== undefined && phrase !== null && phrase.length > 0
      );

    const storedTitles = localStorage.getItem("titleList");
    const titlesFromStorage = storedTitles
      ? (JSON.parse(storedTitles) as string[])
      : [];

    const mergedTitles = Array.from(
      new Set([...phrases, ...titlesFromStorage])
    );

    this.initializeForm(mergedTitles);

    if (mergedTitles.length > 0) {
      this.initializeForm(mergedTitles);
    } else {
      this.byTitleForm = this.fb.group({
        titles: this.fb.array([this.fb.control(null, Validators.required)]),
      });
    }

    const lastTerm = localStorage.getItem("selectedTitleTerm");
    const editingIndex = Number(localStorage.getItem("editingTitleIndex"));

    if (lastTerm) {
      const index = !isNaN(editingIndex) ? editingIndex : 0;

      while (this.titles.length <= index) {
        this.addTitleField();
      }

      this.titles.at(index).patchValue(lastTerm);
    }

    localStorage.removeItem("selectedTitleTerm");
    localStorage.removeItem("editingTitleIndex");
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["filter"] && this.byTitleForm) {
      this.titles.at(0).patchValue(this.filter.searchphrase);
    }
  }

  initializeForm(terms: string[]): void {
    const controls = terms.map((t) =>
      this.fb.control(t, [Validators.required, Validators.minLength(3)])
    );

    if (controls.length === 0) {
      controls.push(
        this.fb.control(null, [Validators.required, Validators.minLength(3)])
      );
    }

    this.byTitleForm = this.fb.group({
      titles: this.fb.array(controls),
    });
  }

  restoreTitles(terms: (string | null)[]): void {
    this.byTitleForm.setControl("titles", this.fb.array([]));
    for (const t of terms) {
      this.titles.push(
        this.fb.control(t, [Validators.required, Validators.minLength(3)])
      );
    }
  }

  apply(): void {
    const phrases: string[] = this.titles.value.filter(
      (val: string | null | undefined): val is string =>
        !!val && val.length >= 3
    );

    if (phrases.length === 0) return;

    localStorage.setItem("titleList", JSON.stringify(phrases));

    let finalFilter: Filter;

    if (phrases.length === 1) {
      finalFilter = new ByTitle(phrases[0]);
    } else {
      finalFilter = new MatchAny(phrases.map((phrase) => new ByTitle(phrase)));
    }

    this.notComponent.filter = finalFilter;
    const modifiedFilter = this.notComponent.getModifiedFilter();

    this.applyFilter.emit(modifiedFilter);
  
  }

  onFilterChanged(finalFilter: Filter) {
    this.applyFilter.emit(finalFilter);
 
  }

  addTitleField(): void {
    this.titles.push(
      this.fb.control(null, [Validators.required, Validators.minLength(3)])
    );

    const currentValues = this.titles.value.filter(
      (v) => v !== null && v.length >= 3
    );
    localStorage.setItem("titleList", JSON.stringify(currentValues));
  }

  setEditingIndex(index: number): void {
    this.editingIndex = index;
    localStorage.setItem("editingTitleIndex", index.toString());
  }

  get titles(): FormArray<FormControl> {
    return this.byTitleForm.get("titles") as FormArray<FormControl>;
  }
}
